import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { useTickets } from 'contexts/TicketsContext';
import type { ExportTicketsRequestDTO } from 'declarations/dto/tickets';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { NoopT } from 'declarations/types/common';
import { useExportTickets } from 'hooks/api/useTickets';

type UseHeaderT = {
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetSearchTerm: NoopT;
  openNewTicketPopup: NoopT;
  openROListPopup: NoopT;
  handleExportClick: NoopT;
  isExportingTickets: boolean;
  searchText: string;
};

const searchBySearchTermDelay = 300;
export const mapSearchParamsToExportRequest = (
  searchParams: URLSearchParams
): ExportTicketsRequestDTO => {
  return {
    roofTopIds: searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
    sorting: {
      parameter: (searchParams.get(TicketsRefinementKeysE.Parameter) as string) || undefined,
      order: (searchParams.get(TicketsRefinementKeysE.Order) as string) || undefined,
    },
    filter: {
      status: (searchParams.get(TicketsRefinementKeysE.Status) as string) || undefined,
      ticketAge: (searchParams.get(TicketsRefinementKeysE.TicketAge) as string) || undefined,
      roId: (searchParams.get(TicketsRefinementKeysE.RONumber) as string) || undefined,
      roNumber: searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
      assigneeId: (searchParams.get(TicketsRefinementKeysE.AssignedToMe) as string) || undefined,
      roofTopId: searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
      assignedToMe: !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
      pastMonth: !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
      isArchived: !!searchParams.get(TicketsRefinementKeysE.IsArchived) || false,
      past30Days: !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
      closedROAgeRange: (searchParams.get(TicketsRefinementKeysE.Schedule) as string) || undefined,
      assigneeName: searchParams.get(TicketsRefinementKeysE.Assignee) || undefined,
      ticketCategoryIds: searchParams.get(TicketsRefinementKeysE.Category) || undefined,
      searchTerm: searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
    },
  };
};

const useHeader = (): UseHeaderT => {
  /**
   * Hooks
   */
  const {
    setNewTicketPopupVisibility,
    setROListPopupVisibility,
    updateSearchParam,
    clearSearchParam,
    getSearchParam,
    selectedTicketsIds,
  } = useTickets();
  const searchTermParam = getSearchParam(TicketsRefinementKeysE.SearchTerm);
  const [searchText, setSearchText] = useState(searchTermParam || '');
  const [searchParams] = useSearchParams();
  /**
   * Handlers
   */
  const debouncedUpdateSearchParam = useDebouncedCallback(
    (value: string) => updateSearchParam(TicketsRefinementKeysE.SearchTerm, value),
    searchBySearchTermDelay
  );
  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const trimmedValue = value.trim();

    setSearchText(value);

    if (trimmedValue.length >= 3) {
      debouncedUpdateSearchParam(value);
    }

    if (!trimmedValue) {
      clearSearchParam(TicketsRefinementKeysE.SearchTerm);
    }
  };
  const openNewTicketPopup = (): void => setNewTicketPopupVisibility(true);
  const openROListPopup = (): void => setROListPopupVisibility(true);
  const resetSearchTerm = (): void => {
    setSearchText('');
    clearSearchParam(TicketsRefinementKeysE.SearchTerm);
  };

  const exportParamsWithIds: ExportTicketsRequestDTO = {
    ...mapSearchParamsToExportRequest(searchParams),
    filter: {
      ...mapSearchParamsToExportRequest(searchParams).filter,
      ticketIds: selectedTicketsIds,
    },
  };

  const exportTickets = useExportTickets(exportParamsWithIds, (result: Blob): void => {
    const fileName = `chirp_tickets_${new Date().toLocaleString()}.csv`;
    const blob = new Blob([result], { type: 'csv/text' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  });

  const handleExportClick = (): void => {
    exportTickets.refetch();
  };

  /**
   * Effects
   */
  useEffect(() => {
    // Clear searchText state value when user clears all filters
    if (!searchTermParam) {
      setSearchText('');
    }
  }, [searchTermParam]);

  return {
    searchText,
    onChangeInput,
    openNewTicketPopup,
    openROListPopup,
    resetSearchTerm,
    handleExportClick,
    isExportingTickets: exportTickets.isFetching,
  };
};

export default useHeader;
